import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BlockIcon from "@mui/icons-material/Block";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import {
  getUsersAccountsList,
  changeUserAccountStatus,
} from "../../../../services/Admin/UsersAccountsApi";
import { useAuth } from "../../../../Context/AuthContext";


const ClintsDataPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchClientsAccounts();
  }, []);

  const fetchClientsAccounts = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getUsersAccountsList();
      setClients(response.data || []);
    } catch (err) {
      console.error("خطأ في جلب حسابات العملاء:", err);
      setError(err.message || "حدث خطأ في جلب حسابات العملاء");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClientStatus = async (client) => {
    setStatusLoadingId(client.id);
    setError("");
    setSuccessMessage("");

    try {
      await changeUserAccountStatus(client.id);
      const actionText = client.is_active === 1 ? "إيقاف" : "تنشيط";
      setSuccessMessage(`تم ${actionText} الحساب بنجاح: ${client.name || `#${client.id}`}`);
      await fetchClientsAccounts();
    } catch (err) {
      console.error("خطأ في تغيير حالة حساب العميل:", err);
      setError(err.message || "حدث خطأ أثناء تغيير حالة حساب العميل");
    } finally {
      setStatusLoadingId(null);
    }
  };

  const filteredClients = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return clients.filter((client) => {
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && client?.is_active === 1) ||
        (statusFilter === "inactive" && client?.is_active === 0);

      if (!matchesStatus) return false;

      const activeStatusText = client?.is_active === 1 ? "نشط" : "غير نشط";
      const createdDate = client?.created_at
        ? new Date(client.created_at).toLocaleDateString("ar-EG")
        : "";

      if (!q) return true;

      const haystack = [
        client?.id,
        client?.name,
        client?.username,
        client?.email,
        client?.phone,
        client?.phone2,
        client?.phone3,
        client?.address,
        client?.address2,
        client?.address3,
        client?.role,
        activeStatusText,
        createdDate,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [clients, searchText, statusFilter]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const activeClients = clients.filter((client) => client.is_active === 1).length;
  const inactiveClients = clients.filter((client) => client.is_active === 0).length;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <PeopleIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          حسابات العملاء
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="ابحث في كل بيانات العميل (الاسم، الإيميل، الهاتف، العنوان، الحالة...)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="client-status-filter-label">حالة الحساب</InputLabel>
          <Select
            labelId="client-status-filter-label"
            value={statusFilter}
            label="حالة الحساب"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">الكل</MenuItem>
            <MenuItem value="active">نشط</MenuItem>
            <MenuItem value="inactive">غير نشط</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {clients.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  إجمالي العملاء
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {clients.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  الحسابات النشطة
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {activeClients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  الحسابات غير النشطة
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {inactiveClients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {clients.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <PeopleIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد حسابات عملاء
          </Typography>
        </Paper>
      ) : filteredClients.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table dir="rtl">
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  العميل
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  البريد الإلكتروني
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الهاتف
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  العنوان
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الحالة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  تاريخ الإنشاء
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center" }}>
                  إجراءات
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredClients.map((client, index) => (
                <TableRow
                  key={client.id}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover" },
                    backgroundColor: index % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        color="default"
                        sx={{ minWidth: 36, fontWeight: "bold" }}
                      />
                      <Avatar
                        sx={{
                          bgcolor: client.is_active === 1 ? "success.main" : "error.main",
                          width: 38,
                          height: 38,
                        }}
                      >
                        {(client.name || "?").charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {client.name || "غير متوفر"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{client.email || "غير متوفر"}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      {client.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{client.phone}</Typography>
                        </Box>
                      )}
                      {client.phone2 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.phone2}
                        </Typography>
                      )}
                      {client.phone3 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.phone3}
                        </Typography>
                      )}
                      {!client.phone && !client.phone2 && !client.phone3 && (
                        <Typography variant="body2" color="text.secondary">
                          غير متوفر
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      {client.address && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Typography variant="body2">{client.address}</Typography>
                        </Box>
                      )}
                      {client.address2 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.address2}
                        </Typography>
                      )}
                      {client.address3 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.address3}
                        </Typography>
                      )}
                      {!client.address && !client.address2 && !client.address3 && (
                        <Typography variant="body2" color="text.secondary">
                          غير متوفر
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    {client.is_active === 1 ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="نشط"
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip
                        icon={<CancelIcon />}
                        label="غير نشط"
                        size="small"
                        color="error"
                      />
                    )}
                  </TableCell>

                  <TableCell sx={{ textAlign: "right" }}>
                    <Typography variant="body2">
                      {new Date(client.created_at).toLocaleDateString("ar-EG", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(client.created_at).toLocaleTimeString("ar-EG", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                  {hasPermission("delete") && (
                    <Tooltip
                      title={client.is_active === 1 ? "إيقاف نشاط الحساب" : "تنشيط الحساب"}
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          color={client.is_active === 1 ? "error" : "success"}
                          disabled={statusLoadingId === client.id}
                          onClick={() => handleToggleClientStatus(client)}
                          sx={{
                            "&:hover": {
                              backgroundColor:
                                client.is_active === 1 ? "error.light" : "success.light",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          {statusLoadingId === client.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : client.is_active === 1 ? (
                            <BlockIcon fontSize="small" />
                          ) : (
                            <CheckCircleIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClintsDataPage;
